import { defineComponent as _defineComponent } from 'vue'
import { createElementVNode as _createElementVNode, normalizeStyle as _normalizeStyle, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, normalizeClass as _normalizeClass, pushScopeId as _pushScopeId, popScopeId as _popScopeId } from "vue"

const _withScopeId = (n: any) => (_pushScopeId("data-v-065eb1e2"),n=n(),_popScopeId(),n)
const _hoisted_1 = ["title"]
const _hoisted_2 = ["viewBox", "v-key"]
const _hoisted_3 = ["href"]
const _hoisted_4 = { key: 1 }
const _hoisted_5 = ["src"]
const _hoisted_6 = ["v-key"]
const _hoisted_7 = ["href"]
const _hoisted_8 = ["v-key"]
const _hoisted_9 = ["href"]
const _hoisted_10 = ["href"]

import { computed, ComputedRef, Ref, ref, watch } from 'vue';

import pinia from '@/stores';
import { useStaticResourcesStore } from '@/stores/StaticResources';

interface Props {
  name: string;
  title?: string;
  showFocus?: boolean;
  showFullColourFocus?: boolean;
  focused?: boolean;
  /**
   * For situations that require icon usage at small sizes,
   * optionally set the following 'heavy' property to true to give the icon a bolder/thicker appearance
   */
  heavy?: boolean;
  fade?: boolean;
  fillColour?: string | null;
  includeViewBox?: boolean;
}
interface Emits {
  (e: 'click'): void,
}

export default /*#__PURE__*/_defineComponent({
  __name: 'BrandIcon',
  props: {
    name: {},
    title: {},
    showFocus: { type: Boolean, default: true },
    showFullColourFocus: { type: Boolean, default: false },
    focused: { type: Boolean, default: false },
    heavy: { type: Boolean, default: false },
    fade: { type: Boolean, default: false },
    fillColour: { default: null },
    includeViewBox: { type: Boolean, default: true }
  },
  emits: ["click"],
  setup(__props: any, { expose: __expose, emit: __emit }) {

const staticResourcesStore = useStaticResourcesStore(pinia);

const props = __props;

const iconElement: Ref<HTMLElement | null> = ref(null);

const emit = __emit;

const isPublicIcon: ComputedRef<boolean> = computed(() => {
  return props.name.startsWith('/public/');
});

const isFlatIcon: ComputedRef<boolean> = computed(() => {
  return props.name.startsWith('flat/');
});

const isFullColour: ComputedRef<boolean> = computed(() => {
  return props.name.includes('fullColour/');
});

const imageId: ComputedRef<string> = computed(() => {
  return props.name.replace(/.*\//, '');
});

const baseImage: ComputedRef<string> = computed(() => {
  return staticResourcesStore.resolvedPaths[`${props.name}.svg`];
});

const focusImageName: ComputedRef<string | undefined> = computed(() => {
  if (!props.name) {
    return;
  }
  if (isFlatIcon.value || isPublicIcon.value || (isFullColour.value && !props.showFullColourFocus)) {
    return;
  }
  if (!props.showFocus) {
    return;
  }
  return `${props.name}-focus.svg`;
});

const focusImage: ComputedRef<string | undefined> = computed(() => {
  if (!focusImageName.value) {
    return;
  }
  return staticResourcesStore.resolvedPaths[focusImageName.value];
});

const propStyles: ComputedRef<Record<string, string>> = computed(() => {
  let styleObject = {};
  if (props.fillColour) {
    styleObject = {
      fill: props.fillColour,
    };
  }
  return styleObject;
});

function focus(): void {
  iconElement.value?.focus();
}

async function resolveImagePath(name: string): Promise<string> {
  let resolved: string = staticResourcesStore.resolvedPaths[name];
  if (!resolved) {
    if (isPublicIcon.value) {
      resolved = name.substring(7);
    } else {
      resolved = await require(`@/assets/images/svg/icon/${name}`);
    }
    staticResourcesStore.setResolvedPath(name, resolved);
  }
  return resolved;
}

watch(() => props.name, (newValue: string) => {
  if (!newValue) {
    return;
  }
  const toResolve: string = `${newValue}.svg`;
  if (isPublicIcon.value) {
    staticResourcesStore.setResolvedPath(toResolve, toResolve.substring(7));
  } else {
    resolveImagePath(toResolve);
  }
}, { immediate: true });

watch(() => focusImageName.value, (newValue?: string) => {
  if (!newValue) {
    return;
  }
  resolveImagePath(newValue);
}, { immediate: true });

__expose({
  focus,
});

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock("span", {
    ref_key: "iconElement",
    ref: iconElement,
    class: _normalizeClass(["brandIconContainer", { showFocus: _ctx.showFocus, focused: _ctx.focused }]),
    title: _ctx.title,
    onClick: _cache[0] || (_cache[0] = ($event: any) => (emit('click')))
  }, [
    (isFlatIcon.value || isPublicIcon.value)
      ? (_openBlock(), _createElementBlock("svg", {
          key: 0,
          viewBox: _ctx.includeViewBox ? '0 0 24 24' : undefined,
          "v-key": _ctx.name,
          class: "icon",
          style: _normalizeStyle(propStyles.value)
        }, [
          _createElementVNode("use", {
            href: `${baseImage.value}#${imageId.value}`
          }, null, 8, _hoisted_3)
        ], 12, _hoisted_2))
      : (isFullColour.value)
        ? (_openBlock(), _createElementBlock("div", _hoisted_4, [
            _createElementVNode("img", {
              src: baseImage.value,
              alt: ""
            }, null, 8, _hoisted_5),
            (_ctx.showFullColourFocus)
              ? (_openBlock(), _createElementBlock("svg", {
                  key: 0,
                  "v-key": _ctx.name,
                  class: _normalizeClass(["icon", {heavy: _ctx.heavy, fade: _ctx.fade}]),
                  style: _normalizeStyle(propStyles.value)
                }, [
                  ((_ctx.showFullColourFocus || _ctx.focused))
                    ? (_openBlock(), _createElementBlock("use", {
                        key: 0,
                        class: "focusIndication",
                        href: `${focusImage.value}#${imageId.value}-focus`
                      }, null, 8, _hoisted_7))
                    : _createCommentVNode("", true)
                ], 14, _hoisted_6))
              : _createCommentVNode("", true)
          ]))
        : (_openBlock(), _createElementBlock("svg", {
            key: 2,
            "v-key": _ctx.name,
            class: _normalizeClass(["icon", {heavy: _ctx.heavy, fade: _ctx.fade}]),
            style: _normalizeStyle(propStyles.value)
          }, [
            (focusImage.value && (_ctx.showFocus || _ctx.focused))
              ? (_openBlock(), _createElementBlock("use", {
                  key: 0,
                  class: "focusIndication",
                  href: `${focusImage.value}#${imageId.value}-focus`
                }, null, 8, _hoisted_9))
              : _createCommentVNode("", true),
            _createElementVNode("use", {
              href: `${baseImage.value}#${imageId.value}`
            }, null, 8, _hoisted_10)
          ], 14, _hoisted_8))
  ], 10, _hoisted_1))
}
}

})